import { Building2, Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';
import { Button } from '@/components/ui/button';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import { sectionPath } from '../place/place';
import { PlaceIcon } from '../ui/PlaceIcon';
import type { TenantRow } from './use-tenants';

function OrganizationsCount({ row }: { readonly row: TenantRow }) {
  const { t } = useTranslation('administration');
  const empty = row.organizations.total === 0;
  return (
    <span className="place-row-count">
      {empty ? (
        <span className="text-muted-foreground text-sm">{t('tenants.noOrganizations')}</span>
      ) : (
        <>
          <span className="place-row-number">
            {t('tenants.organizationsCount', { count: row.organizations.total })}
          </span>
          {row.organizations.active < row.organizations.total ? (
            <span className="people-row-sub">
              {t('tenants.activeCount', { count: row.organizations.active })}
            </span>
          ) : null}
        </>
      )}
    </span>
  );
}

function RowButtons({
  row,
  onRename,
}: {
  readonly row: TenantRow;
  readonly onRename: (() => void) | null;
}) {
  const { t } = useTranslation('administration');
  const tenant = { kind: 'tenant', id: row.id } as const;
  const empty = row.organizations.total === 0;
  return (
    <span className="people-row-actions gap-1">
      {empty && row.can.createOrganization ? (
        <Button asChild variant="outline" size="sm">
          <Link to={`${sectionPath(tenant, 'organizations')}?new=1`}>
            <Plus />
            {t('tenants.createFirst')}
          </Link>
        </Button>
      ) : null}
      {!(empty && row.can.createOrganization) && row.can.administer ? (
        <Button asChild variant="outline" size="sm">
          <Link to={sectionPath(tenant, 'overview')}>{t('organizations.actions.administer')}</Link>
        </Button>
      ) : null}
      {onRename !== null ? (
        <RowActionsMenu
          actions={[
            {
              id: 'rename',
              label: t('organizations.actions.rename'),
              icon: Pencil,
              onSelect: onRename,
            },
          ]}
          label={t('organizations.actions.more', { name: row.name })}
        />
      ) : null}
    </span>
  );
}

export function TenantItem({
  row,
  cardHref,
  onRename,
}: {
  readonly row: TenantRow;
  readonly cardHref: string;
  readonly onRename: (() => void) | null;
}) {
  const { t } = useTranslation('administration');
  const selected = useMatch(cardHref.split('?')[0] ?? cardHref) !== null;

  return (
    <div role="listitem" className={cn('people-row place-row', selected && 'people-row-selected')}>
      <Link
        to={cardHref}
        className="people-row-main"
        aria-current={selected ? 'page' : undefined}
        aria-label={t('tenants.card.open', { name: row.name })}
      >
        <PlaceIcon icon={Building2} />
        <span className="flex min-w-0 flex-col">
          <span className="people-row-name">{row.name}</span>
          <span className="people-row-sub">{t('tenants.kind')}</span>
        </span>
      </Link>
      <OrganizationsCount row={row} />
      <span className="place-row-count place-row-people">
        <span className="place-row-number">
          {t('organizations.peopleCount', { count: row.people })}
        </span>
      </span>
      <span className="people-row-updated">{formatDate(row.createdAt)}</span>
      <RowButtons row={row} onRename={onRename} />
    </div>
  );
}
